import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { DefaultValuesService } from '../../../../services/defaultvalues.service';
import { ToastrService } from 'ngx-toastr';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Utils } from '../../../../utils/utils';
import { TaxPeConfigurationService } from '../../../../services/tax-peconfiguration.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PeConfigurationChangelogComponent } from './pe-configurationchangelog/pe-configurationchangelog.component';
import { MatSelectChange } from '@angular/material/select';
import { FiscalYearUtils } from '../../../../utils/fiscal-year.utils';

@Component({
    selector: 'pe-configuration',
    templateUrl: './pe-configuration.component.html',
    styleUrls: ['./pe-configuration.component.less',
        '../../../../style/tables/table-add-butt-header-orange.less',
        '../../../../style/new-generic-styles.component.less'],
    standalone: false
})
export class PeConfigurationComponent implements OnInit {

    title: string;
    subscriptionManagementId: any;
    columnsToDisplay = ['configField', 'configValue', 'otherFunctionValue', 'actions'];
    configForm!: FormGroup;
    dataSource = new MatTableDataSource<any>();
    enumSelectors: { key: string, values: { id: number | string, text: string }[] }[] = [];
    availableFunctionPe: FunctionPe[] = [];
    availableTypePe: TypePe[] = [];
    availableCurrencies: CurrencyReference[] = [];
    availableCountries: Country[] = [];
    configuration: PeConfiguration = {} as PeConfiguration;
    otherFunctionControl: { ServerValue: string, ConfigValue: string, isOpenOverlay: boolean, MaxLength: number } = {ServerValue: '', ConfigValue: '', isOpenOverlay: false, MaxLength: 100};
    keysToExclude = [
        'peConfigurationId',
        'managementId',
        'createdBy',
        'changedBy',
        'createdAtUtc',
        'changedAtUtc',
        'otherFunction'];
    kesysNotEditable = [
        'mersyNode',
        'uniquePeNumber',
        'companyName',
        'idMotherCompany',
        'nameMotherCompany',
    ];

    inputMaxLength: { configField: string, maxLength: number }[] = [
        { configField: 'location', maxLength: 2 },
        { configField: 'cityOfPE', maxLength: 100 },
        { configField: 'address', maxLength: 200 },
        { configField: 'locationMotherCompany', maxLength: 2 },
        { configField: 'taxIdentificationNumber', maxLength: 50 },
        { configField: 'taxIdentificationNumberMotherCompany', maxLength: 50 },
        { configField: 'currencyLocalLedger', maxLength: 3 },
        { configField: 'currencyTaxReturn', maxLength: 3 },
        { configField: 'sapconsUniqueMgmtID', maxLength: 200 }
    ];

    peConfigurationHistoryDialog: MatDialogRef<PeConfigurationChangelogComponent> | undefined;

    // Store the initial backend value for typeId (as an array of numbers)
    private initialTypeIdSelection: number[] = [];

    // Store the initial backend value for functionId (as an array of numbers)
    private initialFunctionIdSelection: number[] = [];

    constructor(
        public defaults: DefaultValuesService
        , private toasterService: ToastrService
        , private formBuilder: FormBuilder
        , private taxPeConfigurationService: TaxPeConfigurationService
        , private dialogForm: MatDialog
        , private http: HttpClient
        , @Inject('BASE_URL') private baseUrl: string
    ) {
        this.title = ` PE Configuration (${this.defaults.managementName})`;
        this.configForm = this.formBuilder.group({
            configRows: this.formBuilder.array([])
        });
    }

    ngOnInit(): void {
        this.subscriptionManagementId = this.defaults.managementIdMessageChanges$.subscribe(() => { this.loadInformation() });
        this.loadInformation();
        this.getFunctionPe();
        this.getTypePe();
        this.getCurrencyReference();
        this.getAllCountries();
        this.enumSelectors.push({
            key: 'active',
            values: [{ id: 0, text: 'Not Active' },
                { id: 1, text: 'Active' }]
        });
    }

    ngOnDestroy() {
        this.subscriptionManagementId.unsubscribe();
    }

    getFormValue(i: number) {
        return this.configForm?.get('configRows')?.value[i];
    }

    mapConfigurationToForm() {
        this.configForm = this.formBuilder.group({
            configRows: this.formBuilder.array(Object.keys(this.configuration).map((configKey, idx) => {
                const configKeyValue = this.configuration[configKey];
                let enumName: string | null = null;
                switch (configKey) {
                    case 'typeIds':
                        enumName = 'typePe';
                        break;
                    case 'functionIds':
                        enumName = 'functionPe';
                        break;
                    case 'active':
                        enumName = 'active';
                        break;
                    case 'currencyLocalLedgerId':
                    case 'currencyTaxReturnId':
                        enumName = 'currency';
                        break;
                    case 'location':
                    case 'locationMotherCompany':
                        enumName = 'isoCountryCode';
                        break;
                }
                // For typeId and functionId, store as array for multi-select (parse comma-separated string)
                let value;
                switch (configKey) {
                    case 'typeIds':
                    case 'functionIds':
                        value = (typeof configKeyValue === 'string' && configKeyValue.length > 0)
                            ? configKeyValue.split(',').map(x => +x).filter(x => !isNaN(x))
                            : [];
                        break;
                    case 'active':
                        value = configKeyValue ? 1 : 0;
                        break;
                    case 'location':
                    case 'locationMotherCompany':
                        value = configKeyValue;
                        break;
                    default:
                        value = configKeyValue;
                }
                // Save initial selection for typeId and functionId (from backend) only once, when form is mapped
                if (configKey === 'typeIds') {
                    this.initialTypeIdSelection = [...value];
                }
                if (configKey === 'functionIds') {
                    this.initialFunctionIdSelection = [...value];
                }

                const control = (configKey === 'typeIds' || configKey === 'functionIds')
                    ? new FormControl(value, Validators.required)
                    : new FormControl(value);

                return this.formBuilder.group({
                    ConfigField: new FormControl(configKey),
                    ConfigFieldPretty: new FormControl(this.prettyFormatConfigField(configKey)),
                    ConfigValue: control,
                    Type: new FormControl(this.getPropertyType(configKey)),
                    EnumName: new FormControl(enumName),
                    OnEdit: new FormControl(false),
                    CanEdit: new FormControl(!this.kesysNotEditable.includes(configKey)),
                    MaxLength: new FormControl(this.inputMaxLength.find(x => x.configField === configKey)?.maxLength || 200),
                });
            }))
        });
    }

    mapFormToConfiguration(): PeConfiguration {
        const configRows = this.configForm.get('configRows') as FormArray;
        let updatedConfiguration: PeConfiguration = { ...this.configuration };

        configRows.value.forEach((val, index) => {
            const configField = val['ConfigField'];
            const configValue = val['ConfigValue'];
            const type = val['Type'];

            if (configField) {
                if (configField === 'typeIds' || configField === 'functionIds') {
                    // Convert array to comma-separated string
                    updatedConfiguration[configField] = Array.isArray(configValue) ? configValue.join(',') : '';
                } else if (type === 'enum' && configField === 'active') {
                    updatedConfiguration[configField] = configValue === 1;
                } else {
                    updatedConfiguration[configField] = configValue;
                }
            }
        });
        // If any selected function is 'Other', set otherFunction to the value of the otherFunction control
        let enumValues = this.enumSelectors.find(selector => selector.key === 'functionPe')?.values || [];
        const selectedFunctionIds = updatedConfiguration.functionIds
            ? updatedConfiguration.functionIds.split(',').map(x => +x)
            : [];
        if (enumValues.some(e => selectedFunctionIds.includes(e.id as number) && e.text === 'Other')) {
            updatedConfiguration.otherFunction = this.otherFunctionControl.ConfigValue || undefined;
        } else {
            updatedConfiguration.otherFunction = undefined;
        }
        return updatedConfiguration;
    }

    isFunctionIdOther(i: number): boolean {
        let enumValues = this.getSelectorValues(i);
        let value = this.getFormValue(i);
        if (value.ConfigField === 'functionIds' && Array.isArray(value.ConfigValue)) {
            return value.ConfigValue
                .map((id: number) => enumValues.find(e => e.id === id)?.text)
                .includes('Other');
        }
        return false;
    }

    onRowEditInit(index: number) {
        var configRows = this.configForm.get('configRows') as FormArray;
        var row = configRows.at(index);
        row.get('OnEdit')?.patchValue(true);
    }

    onRowEditCancel(index: number) {
        const configRows = this.configForm.get('configRows') as FormArray;
        const configKey = configRows.at(index).get('ConfigField')?.value;
        const previousConfigValue = this.configuration[configKey];
        const formattedValue = configKey === 'active' ? (previousConfigValue ? 1 : 0) : previousConfigValue;

        // Restore initial backend value for typeId or functionId if discarding changes
        if (configKey === 'typeIds') {
            configRows.at(index)?.get('ConfigValue')?.setValue(
                this.initialTypeIdSelection,
                { emitEvent: false }
            );
        } else if (configKey === 'functionIds') {
            configRows.at(index)?.get('ConfigValue')?.setValue(
                this.initialFunctionIdSelection,
                { emitEvent: false }
            );
        } else {
            configRows.at(index)?.get('ConfigValue')?.setValue(formattedValue);
        }

        configRows.at(index)?.get('OnEdit')?.setValue(false);
        if (configKey === 'functionIds') {
            this.otherFunctionControl.ConfigValue = this.otherFunctionControl.ServerValue;
        }
    }

    onCancel() {
        if (!this.anyChange()) {
            return;
        }
        this.otherFunctionControl.ConfigValue = this.otherFunctionControl.ServerValue;
        this.mapConfigurationToForm();
        this.dataSource = new MatTableDataSource((this.configForm.get('configRows') as FormArray).controls);
    }

    onSave() {
        if (this.anyChange()) {
            const updatedConfiguration = this.mapFormToConfiguration();
            this.taxPeConfigurationService.updatePeConfiguration(
                this,
                updatedConfiguration,
                function (tthis: PeConfigurationComponent, result: PeConfiguration) {
                   if (result) {
                      tthis.otherFunctionControl.ConfigValue = result['otherFunction'] || '';
                      tthis.otherFunctionControl.ServerValue = result['otherFunction'] || '';
                      tthis.keysToExclude.forEach(key => {
                          delete result[key];
                      });

                      tthis.configuration = result;
                      tthis.mapConfigurationToForm();
                      tthis.dataSource = new MatTableDataSource((tthis.configForm.get('configRows') as FormArray).controls);
                      tthis.toasterService.success('PE Configuration was updated successfully!');
                  }
                },
                error => {
                    this.toasterService.error("Failed to update PE Configuration");
                }
            );
        }
    }

    getFunctionPe() {
        this.taxPeConfigurationService.getFunctionPe(
            this,
            function (tthis: PeConfigurationComponent, result: FunctionPe[]) {
                tthis.availableFunctionPe = result;
                tthis.enumSelectors.push({
                    key: 'functionPe',
                    values: tthis.availableFunctionPe.map(item => ({ id: item.functionPeId, text: item.functionPe }))
                });
            },
            null
        );

    }

    getTypePe() {
        this.taxPeConfigurationService.getTypePe(
            this,
            function (tthis: PeConfigurationComponent, result: TypePe[]) {
                tthis.availableTypePe = result;
                tthis.enumSelectors.push({
                    key: 'typePe',
                    values: tthis.availableTypePe.map(item => ({ id: item.typeId, text: item.typePe }))
                });
            },
            null
        );

    }

    anyChange() {
        return this.configForm.value.configRows.filter(x => x.OnEdit).length > 0;
    }

    getSelectorValues(i: number) {
        return this.enumSelectors.find(selector => selector.key === this.getFormValue(i).EnumName)?.values || [];
    }

    getSelectorText(i: number, opts?: { forTooltip?: boolean }) {
        let value = this.getFormValue(i);
        // Special handling for functionId multi-select
        if (value.ConfigField === 'functionIds' && Array.isArray(value.ConfigValue)) {
            const selectedIds = value.ConfigValue;
            const selectedNames = this.getSelectorValues(i)
                .filter(val => selectedIds.includes(val.id))
                .map(val => val.text);

            if (opts?.forTooltip) {
                // Always show full list for tooltip
                return selectedNames.join(', ') || 'N/A';
            }
            if (selectedNames.length > 1) {
                return `${selectedNames.length} selected`;
            }
            return selectedNames[0] || 'N/A';
        }
        // For typeId and other multi-selects, keep original behavior
        if (value.ConfigField === 'typeIds' && Array.isArray(value.ConfigValue)) {
            const selectedIds = value.ConfigValue;
            return this.getSelectorValues(i)
                .filter(val => selectedIds.includes(val.id))
                .map(val => val.text)
                .join(', ') || 'N/A';
        }
        let isBool = value.Type === 'boolean';
        let idVal = isBool ? value.ConfigValue ? 1 : 0 : value.ConfigValue;
        return this.getSelectorValues(i)?.find(val => val.id === idVal)?.text || 'N/A';
    }

    getDateUTCChange(event, i: number) {
        let utcDate = Utils.dateUTCchange(event);
        this.getFormValue(i).ConfigValue = utcDate;
        
        if (this.getFormValue(i).ConfigField === 'disposalDate') {
            this.checkDisposalDateAndUpdateActive(event.value);
        }
    }

    onOtherFunctionChange(value: string) {
        this.otherFunctionControl.ConfigValue = value;
    }

    onEnumFieldChange(event: any, rowIndex: number) {
        const formValue = this.getFormValue(rowIndex);
        
        if (formValue.ConfigField === 'active' && event.value === 1) {
            this.clearDisposalDateField();
        }
    }

    private clearDisposalDateField() {
        const configRows = this.configForm?.get('configRows') as FormArray;
        if (!configRows) return;
        
        const disposalIndex = configRows.controls.findIndex((_, i) => 
            this.getFormValue(i).ConfigField === 'disposalDate' && this.getFormValue(i).ConfigValue
        );
        
        if (disposalIndex >= 0) {
            configRows.at(disposalIndex).get('ConfigValue')?.setValue(null);
            this.toasterService.info('Disposal date automatically cleared when setting status to Active.');
        }
    }

    checkUserHistory(event: any) {
        event.preventDefault();
        this.peConfigurationHistoryDialog = this.dialogForm.open(PeConfigurationChangelogComponent, {
            height: '525px',
            width: '1500px'
        });
        this.peConfigurationHistoryDialog.afterClosed().subscribe(() => {
        });
    }

    getCurrencyReference() {
        this.taxPeConfigurationService.getCurrencyReference(
            this,
            function (tthis: PeConfigurationComponent, result: CurrencyReference[]) {
                tthis.availableCurrencies = result;
                tthis.enumSelectors.push({
                    key: 'currency',
                    values: tthis.availableCurrencies.map(item => ({ id: item.currencyRefId, text: item.currency }))
                });
            },
            null
        );

    }

    getAllCountries() {
        const excludedCountryCodes = ['ZZ', 'TA', 'TE', 'TP', '  '];

        Utils.httpGetAllCountries(
            this.http,
            this.baseUrl,
            this,
            function (tthis: PeConfigurationComponent, result: Country[]) {
                tthis.availableCountries = result.filter(x => !excludedCountryCodes.includes(x.CountryCode));
                tthis.enumSelectors.push({
                    key: 'isoCountryCode',
                    values: tthis.availableCountries
                        .sort((a, b) => a.CountryCode.localeCompare(b.CountryCode))
                        .map(country => ({ id: country.CountryCode, text: country.CountryCode }))
                });
            }
        );
    }

    /**
     * Prevent unselecting the last value in the typeId or functionId multi-select.
     */
    onTypeIdSelectionChange(event: any, i: number) {
        const configRows = this.configForm.get('configRows') as FormArray;
        const control = configRows.at(i).get('ConfigValue');
        if (!control) {
            return;
        }
        const currentSelection = control.value as number[];

        if (currentSelection.length === 0) {
            // Restore initial backend value if user tries to unselect the last one
            if (this.getFormValue(i).ConfigField === 'typeIds') {
                control.setValue(this.initialTypeIdSelection, { emitEvent: false });
            } else if (this.getFormValue(i).ConfigField === 'functionIds') {
                control.setValue(this.initialFunctionIdSelection, { emitEvent: false });
            }
            return;
        }
        // No need to update any previous selection here
    }

    private loadInformation(): void {
        this.title = ` PE Configuration (${this.defaults.managementName})`;
        this.taxPeConfigurationService.getPeConfiguration(
            this,
            function (tthis: PeConfigurationComponent, result: PeConfiguration) {

                tthis.otherFunctionControl.ConfigValue = result['otherFunction'] || '';
                tthis.otherFunctionControl.ServerValue = result['otherFunction'] || '';
              
                tthis.keysToExclude.forEach(key => {                   
                    delete result[key];
                });
               
                tthis.configuration = result;
                tthis.mapConfigurationToForm();
                tthis.dataSource = new MatTableDataSource((tthis.configForm.get('configRows') as FormArray).controls);
            },
            error => {
                this.toasterService.error("Unable to load Pe configuration");
            }
        );
    }

    private prettyFormatConfigField(configKey: string): string {
        if (configKey === 'active')
            configKey = 'status';
        else if (configKey === 'typeIds')
            configKey = 'type';
        else if (configKey === 'functionIds')
            configKey = 'functionPE';
        else if (configKey === 'sapconsUniqueMgmtID')
            return 'SAP@Kons Unique Mgt-ID';
        else if (configKey === 'mersyNode')
            return 'MERSY Node';
        else if (configKey === 'uniquePeNumber')
            return 'Unique PE Number';
        else if (configKey === 'idMotherCompany')
            return 'Mother Company ID';
        else if (configKey === 'nameMotherCompany')
            return 'Mother Company Name';
        else if (configKey === 'currencyLocalLedgerId')
            configKey = 'currencyLocalLedger';
        else if (configKey === 'currencyTaxReturnId')
            configKey = 'currencyTaxReturn';
        // Convert camelCase to a more readable format
        return configKey
            .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
            .replace(/_/g, ' ') // Replace underscores with spaces
            .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize the first letter of each word
    }

    private getPropertyType(configKey: string): string {
        const configKeyValue = this.configuration[configKey];
        if (configKey === 'typeIds' || configKey === 'functionIds') {
            return 'enum-multi';
        }
        let isEnumType = (configKey === 'active') || configKey === 'currencyLocalLedgerId' || configKey === 'currencyTaxReturnId' || configKey === 'location' || configKey === 'locationMotherCompany';
        if (isEnumType)
            return 'enum';
        else if (configKey === 'establishmentDate' || configKey === 'disposalDate')
            return 'date';
        else
            return 'string';
    }

    private checkDisposalDateAndUpdateActive(disposalDate: Date | null) {
        if (!disposalDate) return;

        const currentFiscalYear = FiscalYearUtils.getCurrentFiscalYear();
        const disposalFiscalYear = FiscalYearUtils.getFiscalYearFromDate(disposalDate);

        if (disposalFiscalYear < currentFiscalYear && this.getActiveFieldValue() !== 0) {
            this.setActiveFieldValue(0);
            this.toasterService.info('Status automatically set to "Not Active" due to disposal date in past fiscal year.');
        }
    }

    private getActiveFieldValue(): number | null {
        const configRows = this.configForm?.get('configRows') as FormArray;
        if (!configRows) return null;
        
        const activeIndex = configRows.controls.findIndex((_, i) => this.getFormValue(i).ConfigField === 'active');
        return activeIndex >= 0 ? configRows.at(activeIndex).get('ConfigValue')?.value : null;
    }

    private setActiveFieldValue(value: number) {
        const configRows = this.configForm.get('configRows') as FormArray;
        const activeIndex = configRows.controls.findIndex((_, i) => this.getFormValue(i).ConfigField === 'active');
        
        if (activeIndex >= 0) {
            configRows.at(activeIndex).get('ConfigValue')?.setValue(value);
        }
    }

}
